'use client';

import { useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { setShallowSearchParams, useShallowSearchParam } from '@/lib/hooks/use-shallow-search-param';
import { readBeachSelection, type BeachSelection } from '@/lib/beach-explorer/selection';
import { beachTimeAt } from '@/lib/beach-explorer/forecast/model';
import type { ExplorerLocation } from '@/lib/beach-explorer/locations/registry';
import type { ScoreCurves } from './BeachScores';

const BeachExplorerDialog = dynamic(() => import('./BeachExplorerDialog'), { ssr: false });
export type BeachExplorerProps = { location: ExplorerLocation; selection: BeachSelection; sport: string; sports: readonly string[];
  scoresByDate: Record<string, ScoreCurves>; onSelection(selection: BeachSelection): void; onSport(sport: string): void };

export default function BeachExplorerEntry(props: BeachExplorerProps) {
  const [open, setOpen] = useState(false);
  const entry = useRef<HTMLButtonElement>(null);
  const selectedDate = useShallowSearchParam('forecastDate'), selectedHour = useShallowSearchParam('forecastHour');
  const enterBeach = () => {
    const now = beachTimeAt(Date.now(), props.location.timezone);
    // Refresh the default at the actual click, even if this page has sat open.
    // Explicit page selections and shared forecast links still take precedence.
    props.onSelection(readBeachSelection(selectedDate, selectedHour, now.date, now.hour));
    setOpen(true);
    setShallowSearchParams({ beach: 'explore' });
  };
  return <section className="mt-5 overflow-hidden rounded-[18px] border border-border bg-navy text-white" aria-label={`Explore ${props.location.name} beach`}>
    <div className="flex flex-col sm:flex-row">
      <div className="relative h-32 shrink-0 sm:h-auto sm:w-44"><Image src={props.location.image} alt={props.location.imageAlt ?? `${props.location.name} coastline`} fill sizes="(max-width: 640px) 100vw, 176px" className="object-cover" /></div>
      <div className="flex flex-1 flex-wrap items-center justify-between gap-4 p-5">
        <div><h3 className="font-heading text-xl font-bold">Step onto {props.location.name}</h3><p className="mt-1 max-w-md text-sm text-white/75">Explore the bay and watch your selected forecast come to life.</p><p className="mt-2 text-xs text-white/65">Forecast simulation · {props.selection.date} · {String(props.selection.hour).padStart(2, '0')}:00</p></div>
        <button ref={entry} type="button" onClick={enterBeach} className="min-h-12 rounded-xl bg-white px-5 py-3 text-sm font-bold text-navy focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-light">Enter the beach <span aria-hidden="true">↗</span></button>
      </div>
    </div>
    {open && <BeachExplorerDialog {...props} onClose={() => { setOpen(false); setShallowSearchParams({ beach: null, beachView: null }); requestAnimationFrame(() => entry.current?.focus()); }} />}
  </section>;
}
